import React from 'react';
import {
  clubFeedNeedsFillCount,
  clubFeedTotalCopy,
  clubFeederBarTone,
  clubFeederDaysLabel,
  clubFeederFillPercent,
  clubFeederLastFillLine,
  clubFeederMeta,
} from '../utils/clubWaypointBoard';
import { clubFillCanLog } from '../utils/clubLogFill';
import './ClubMapBoards.css';
import './ClubLogFillDialog.css';

export function ClubFeederBoard({
  feeders = [],
  hoveredWaypointId = null,
  onHoverWaypoint,
  onLogFill,
  onEditWaypoint,
  onMoveWaypoint,
  onDeleteWaypoint,
  now,
}) {
  const need = clubFeedNeedsFillCount(feeders);

  return (
    <section className="club-feeder-board" data-testid="club-feeder-board" aria-label="Feeders">
      <header className="club-feeder-board__header">
        <span className="club-feeder-board__title">FEEDERS</span>
        {need > 0 ? (
          <span className="club-feeder-board__need" data-testid="club-feeder-need">
            {need} NEEDS FILL
          </span>
        ) : null}
      </header>
      {feeders.length === 0 ? (
        <p className="club-feeder-board__empty" data-testid="club-feeder-empty">
          No feeders yet. Your president adds them on the club map.
        </p>
      ) : (
        <ul className="club-feeder-board__list">
          {feeders.map((station) => {
            const tone = clubFeederBarTone(station);
            const hovered =
              hoveredWaypointId != null && Number(hoveredWaypointId) === Number(station.id);
            return (
              <li key={station.id}>
                <div
                  role="presentation"
                  className={[
                    'club-feeder-card',
                    `club-feeder-card--${tone}`,
                    hovered ? 'club-feeder-card--hovered' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-testid={`club-feeder-card-${station.id}`}
                  onMouseEnter={() => onHoverWaypoint && onHoverWaypoint(station.id)}
                  onMouseLeave={() => onHoverWaypoint && onHoverWaypoint(null)}
                >
                  <div className="club-feeder-card__top">
                    <span className="club-feeder-card__name">{station.name}</span>
                    <span className="club-feeder-card__days" data-tone={tone}>
                      {clubFeederDaysLabel(station, now)}
                    </span>
                  </div>
                  <div className="club-feeder-card__meta">{clubFeederMeta(station)}</div>
                  <div className="club-feeder-card__bar" aria-hidden>
                    <div
                      className="club-feeder-card__bar-fill"
                      data-tone={tone}
                      style={{ width: `${clubFeederFillPercent(station)}%` }}
                    />
                  </div>
                  <div className="club-feeder-card__last">
                    {clubFeederLastFillLine(station, now)}
                  </div>
                  {clubFillCanLog(station) || onEditWaypoint || onMoveWaypoint || onDeleteWaypoint ? (
                    <div className="club-feeder-card__acts">
                      {clubFillCanLog(station) ? (
                        <button
                          type="button"
                          className="club-feeder-card__log"
                          data-testid={`club-feeder-log-${station.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onLogFill?.(station);
                          }}
                        >
                          LOG FILL
                        </button>
                      ) : null}
                      {onMoveWaypoint ? (
                        <button
                          type="button"
                          className="club-feeder-card__edit"
                          data-testid={`club-feeder-move-${station.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onMoveWaypoint(station);
                          }}
                        >
                          MOVE
                        </button>
                      ) : null}
                      {onEditWaypoint ? (
                        <button
                          type="button"
                          className="club-feeder-card__edit"
                          data-testid={`club-feeder-edit-${station.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditWaypoint(station);
                          }}
                        >
                          EDIT
                        </button>
                      ) : null}
                      {onDeleteWaypoint ? (
                        <button
                          type="button"
                          className="club-feeder-card__edit club-feeder-card__edit--danger"
                          data-testid={`club-feeder-delete-${station.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteWaypoint(station);
                          }}
                        >
                          DELETE
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function ClubMarkerPanel({ markers = [], onEditWaypoint, onMoveWaypoint, onDeleteWaypoint }) {
  if (!markers.length) return null;
  return (
    <section className="club-map-markers" data-testid="club-map-markers" aria-label="Markers">
      <div className="club-map-markers__title">MARKERS</div>
      {markers.map((marker) => (
        <div key={marker.id} className="club-map-markers__row" data-testid={`club-map-marker-${marker.id}`}>
          <span className="club-map-markers__dot" aria-hidden />
          <span className="club-map-markers__name">{marker.name || 'Marker'}</span>
          {onMoveWaypoint ? (
            <button
              type="button"
              className="club-map-markers__edit"
              data-testid={`club-map-marker-move-${marker.id}`}
              onClick={() => onMoveWaypoint(marker)}
            >
              MOVE
            </button>
          ) : null}
          {onEditWaypoint ? (
            <button
              type="button"
              className="club-map-markers__edit"
              data-testid={`club-map-marker-edit-${marker.id}`}
              onClick={() => onEditWaypoint(marker)}
            >
              EDIT
            </button>
          ) : null}
          {onDeleteWaypoint ? (
            <button
              type="button"
              className="club-map-markers__edit club-map-markers__edit--danger"
              data-testid={`club-map-marker-delete-${marker.id}`}
              onClick={() => onDeleteWaypoint(marker)}
            >
              DELETE
            </button>
          ) : null}
        </div>
      ))}
      {onEditWaypoint ? null : (
        <div className="club-map-markers__hint">Read only. President edits from the list.</div>
      )}
    </section>
  );
}

export function ClubFeedTotalCard({ stationCount = 0, seasonLbs = 0, memberCount = null }) {
  const copy = clubFeedTotalCopy({ stationCount, seasonLbs, memberCount });
  return (
    <section className="club-feed-total" data-testid="club-feed-total" aria-label="Club total">
      <div className="club-feed-total__kicker">CLUB TOTAL</div>
      <div className="club-feed-total__lbs">
        {copy.lbsLabel}
        <span className="club-feed-total__unit">LB THIS SEASON</span>
      </div>
      <div className="club-feed-total__detail">{copy.detail}</div>
    </section>
  );
}

export default ClubFeederBoard;
